const BASE_URL = '/api/v1';
const AUTH_FLAG_KEY = 'dp_awards_authenticated';
const USER_INFO_KEY = 'dp_awards_user';

let onUnauthorized: (() => void) | null = null;

/** Registers a callback invoked when a 401 response is received. */
export function registerUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

/** Returns whether the user has an active session (UI flag only; actual auth is cookie-based). */
export function isStoredAuthenticated(): boolean {
  return localStorage.getItem(AUTH_FLAG_KEY) === 'true';
}

/** Marks the session as active in local storage (UI flag only). */
export function setAuthenticated(): void {
  localStorage.setItem(AUTH_FLAG_KEY, 'true');
}

/** Clears the session flag from local storage. */
export function clearAuthenticated(): void {
  localStorage.removeItem(AUTH_FLAG_KEY);
  localStorage.removeItem(USER_INFO_KEY);
}

/** Stores user info in local storage for UI persistence. */
export function storeUserInfo(user: {
  id: string;
  email: string;
  name: string;
  role: string;
  country: string | null;
}): void {
  localStorage.setItem(USER_INFO_KEY, JSON.stringify(user));
}

/** Retrieves stored user info from local storage. */
export function getStoredUserInfo(): {
  id: string;
  email: string;
  name: string;
  role: string;
  country: string | null;
} | null {
  const raw = localStorage.getItem(USER_INFO_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as {
      id: string;
      email: string;
      name: string;
      role: string;
      country: string | null;
    };
  } catch {
    return null;
  }
}

/** Represents a standardized API error response. */
export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, body: unknown) {
    const message =
      typeof body === 'object' && body !== null && 'message' in body
        ? String((body as Record<string, unknown>).message)
        : `Request failed with status ${status}`;

    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    if (response.status === 401) {
      const wasAuthenticated = isStoredAuthenticated();
      clearAuthenticated();
      if (wasAuthenticated) {
        onUnauthorized?.();
      }
    }

    const text = await response.text();
    let body: unknown;
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
    throw new ApiError(response.status, body);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

function buildUrl(path: string): string {
  return `${BASE_URL}${path}`;
}

/** Performs a GET request to the given API path. */
export async function get<T>(path: string): Promise<T> {
  const response = await fetch(buildUrl(path), {
    method: 'GET',
    credentials: 'include',
  });

  return handleResponse<T>(response);
}

/** Performs a POST request to the given API path with a JSON body. */
export async function post<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(buildUrl(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });

  return handleResponse<T>(response);
}

/** Performs a PUT request to the given API path with a JSON body. */
export async function put<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(buildUrl(path), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });

  return handleResponse<T>(response);
}

/** Performs a DELETE request to the given API path. */
export async function del<T = void>(path: string): Promise<T> {
  const response = await fetch(buildUrl(path), {
    method: 'DELETE',
    credentials: 'include',
  });

  return handleResponse<T>(response);
}
